import { Injectable } from '@angular/core';
import { SwPush, SwUpdate } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  readonly VAPID_PUBLIC_KEY = 'BKagOny0KF_L9B4OPad0Kh9yjuiW5YVGJGp_PwVjVOHU6_V8emEWutolDKjeZ82yJk_EcdT7Xf60jfn83G9XPfU';
  
  private notificationPermission: NotificationPermission = 'default';

  constructor(
    private swPush: SwPush,
    private swUpdate: SwUpdate,
    private http: HttpClient
  ) {
    this.init();
  }

  private init(): void {
    // Vérifier si le navigateur supporte les notifications
    if ('Notification' in window) {
      this.notificationPermission = Notification.permission;
    }

    // Vérifier les mises à jour du service worker
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(event => {
        switch (event.type) {
          case 'VERSION_READY':
            console.log('Nouvelle version disponible');
            this.promptUserToUpdate();
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.error('Échec de l\'installation de la nouvelle version');
            break;
        }
      });

      // Vérifier les mises à jour toutes les heures
      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 60 * 60 * 1000);
    }

    // Écouter les messages push
    if (this.swPush.isEnabled) {
      this.swPush.messages.subscribe(message => {
        console.log('Message push reçu:', message);
        this.handlePushMessage(message);
      });

      this.swPush.notificationClicks.subscribe(click => {
        console.log('Notification cliquée:', click);
        this.handleNotificationClick(click);
      });
    }
  }

  /**
   * Demander la permission pour les notifications
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Ce navigateur ne supporte pas les notifications desktop');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission;
      
      if (permission === 'granted') {
        console.log('Permission accordée pour les notifications');
        return true;
      } else if (permission === 'denied') {
        console.warn('Permission refusée pour les notifications');
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      return false;
    }
    
    return false;
  }

  /**
   * S'abonner aux notifications push
   */
  async subscribeToNotifications(): Promise<void> {
    if (!this.swPush.isEnabled) {
      console.warn('Service Worker Push n\'est pas activé');
      return;
    }

    try {
      // Demander la permission si nécessaire
      if (this.notificationPermission !== 'granted') {
        const granted = await this.requestNotificationPermission();
        if (!granted) {
          throw new Error('Permission refusée pour les notifications');
        }
      }

      // S'abonner aux notifications push
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: this.VAPID_PUBLIC_KEY
      });

      // Envoyer la subscription au serveur
      await this.sendSubscriptionToServer(subscription).toPromise();
      
      console.log('Abonnement aux notifications réussi');
      
      // Afficher une notification de test
      this.showLocalNotification(
        'Notifications activées',
        'Vous recevrez maintenant des notifications de Vendeur.site',
        {
          icon: '/assets/icons/icon-192x192.png',
          badge: '/assets/icons/icon-96x96.png'
        }
      );
    } catch (error) {
      console.error('Erreur lors de l\'abonnement aux notifications:', error);
      throw error;
    }
  }

  /**
   * Se désabonner des notifications push
   */
  async unsubscribeFromNotifications(): Promise<void> {
    if (!this.swPush.isEnabled) {
      return;
    }

    try {
      await this.swPush.unsubscribe();
      console.log('Désabonnement des notifications réussi');
    } catch (error) {
      console.error('Erreur lors du désabonnement:', error);
      throw error;
    }
  }

  /**
   * Vérifier si l'utilisateur est abonné aux notifications
   */
  isSubscribed(): Observable<boolean> {
    if (!this.swPush.isEnabled) {
      return from([false]);
    }
    return this.swPush.subscription.pipe(
      map(sub => sub !== null)
    );
  }

  /**
   * Envoyer la subscription au serveur
   */
  private sendSubscriptionToServer(subscription: PushSubscription): Observable<any> {
    const url = `${environment.apiUrl}/api/notifications/subscribe`;
    return this.http.post(url, subscription).pipe(
      catchError(error => {
        console.error('Erreur lors de l\'envoi de la subscription au serveur:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Afficher une notification locale
   */
  showLocalNotification(title: string, body: string, options?: any): void {
    if (this.notificationPermission !== 'granted') {
      console.warn('Permission non accordée pour afficher des notifications');
      return;
    }

    const defaultOptions: any = {
      body,
      icon: '/assets/icons/icon-192x192.png',
      badge: '/assets/icons/icon-96x96.png',
      vibrate: [200, 100, 200],
      tag: 'vendeur-notification',
      requireInteraction: false,
      silent: false,
      ...options
    };

    if (this.swPush.isEnabled) {
      // Utiliser le service worker pour afficher la notification
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, defaultOptions);
      });
    } else {
      // Fallback: utiliser l'API Notification directement
      new Notification(title, defaultOptions);
    }
  }

  /**
   * Gérer les messages push reçus
   */
  private handlePushMessage(message: any): void {
    console.log('Traitement du message push:', message);
    
    // Extraire les données du message
    const { title, body, data, ...options } = message;
    
    // Afficher la notification
    this.showLocalNotification(
      title || 'Nouvelle notification',
      body || 'Vous avez reçu une nouvelle notification',
      {
        data,
        ...options
      }
    );
  }

  /**
   * Gérer les clics sur les notifications
   */
  private handleNotificationClick(click: any): void {
    console.log('Gestion du clic sur la notification:', click);
    
    // Naviguer vers une page spécifique si nécessaire
    if (click.notification?.data?.url) {
      window.open(click.notification.data.url, '_self');
    }
  }

  /**
   * Demander à l'utilisateur de mettre à jour l'application
   */
  private promptUserToUpdate(): void {
    if (confirm('Une nouvelle version de l\'application est disponible. Voulez-vous mettre à jour maintenant ?')) {
      window.location.reload();
    }
  }

  /**
   * Envoyer une notification de test
   */
  sendTestNotification(): void {
    this.showLocalNotification(
      'Notification de test',
      'Ceci est une notification de test de Vendeur.site',
      {
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/icon-96x96.png',
        actions: [
          { action: 'view', title: 'Voir' },
          { action: 'close', title: 'Fermer' }
        ],
        data: {
          url: '/commande'
        }
      }
    );
  }

  /**
   * Obtenir le statut actuel des permissions
   */
  getPermissionStatus(): NotificationPermission {
    return this.notificationPermission;
  }

  /**
   * Vérifier si les notifications sont supportées
   */
  isNotificationSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }
}